/**
 * Géométrie 2D/3D minimale partagée : polylignes paramétrées par longueur,
 * distances point-segment, intersections. Pas de dépendance à Three.js : le
 * serveur l'utilise aussi.
 */

/** Position au sol (x, z). */
export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

export function dist2(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

export function sub2(a: Vec2, b: Vec2): Vec2 {
  return [a[0] - b[0], a[1] - b[1]];
}

export function norm2(v: Vec2): Vec2 {
  const l = Math.hypot(v[0], v[1]) || 1;
  return [v[0] / l, v[1] / l];
}

/** Distance d'un point au segment [a, b] (plan horizontal). */
export function distPointSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const l2 = dx * dx + dz * dz;
  let t = l2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dz * t));
}

/** Distance d'un point à une polyligne. */
export function distPointPolyline(p: Vec2, pts: readonly Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const d = distPointSegment(p, pts[i] as Vec2, pts[i + 1] as Vec2);
    if (d < best) best = d;
  }
  return best;
}

function orient(a: Vec2, b: Vec2, c: Vec2): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** Intersection stricte de deux segments. */
export function segmentsIntersect(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

/** Distance minimale entre deux segments (0 s'ils se coupent). */
export function distSegmentSegment(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number {
  if (segmentsIntersect(a, b, c, d)) return 0;
  return Math.min(distPointSegment(a, c, d), distPointSegment(b, c, d), distPointSegment(c, a, b), distPointSegment(d, a, b));
}

/**
 * Polyligne 3D paramétrée par abscisse curviligne. `at(s)` est en O(1)
 * amorti grâce au curseur `hint` (les mobiles avancent de façon monotone).
 */
export class Path3 {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  readonly zs: Float64Array;
  /** Abscisse cumulée à chaque sommet. */
  readonly cum: Float64Array;
  readonly length: number;

  constructor(points: readonly Vec3[]) {
    const n = points.length;
    this.xs = new Float64Array(n);
    this.ys = new Float64Array(n);
    this.zs = new Float64Array(n);
    this.cum = new Float64Array(n);
    let acc = 0;
    for (let i = 0; i < n; i++) {
      const p = points[i] as Vec3;
      this.xs[i] = p[0];
      this.ys[i] = p[1];
      this.zs[i] = p[2];
      if (i > 0) {
        acc += Math.hypot(p[0] - (this.xs[i - 1] as number), p[1] - (this.ys[i - 1] as number), p[2] - (this.zs[i - 1] as number));
      }
      this.cum[i] = acc;
    }
    this.length = acc;
  }

  /** Index du segment contenant l'abscisse s (recherche dichotomique). */
  segmentAt(s: number): number {
    const c = this.cum;
    const n = c.length;
    if (s <= 0) return 0;
    if (s >= this.length) return n - 2;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((c[mid] as number) <= s) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /**
   * Écrit dans `out` la position [x, y, z] et la direction unitaire [dx, dz]
   * (plan horizontal) à l'abscisse s.
   */
  sample(s: number, out: Float64Array | number[]): void {
    const i = this.segmentAt(s);
    const s0 = this.cum[i] as number;
    const s1 = this.cum[i + 1] as number;
    const t = s1 > s0 ? Math.min(1, Math.max(0, (s - s0) / (s1 - s0))) : 0;
    const x0 = this.xs[i] as number;
    const z0 = this.zs[i] as number;
    const x1 = this.xs[i + 1] as number;
    const z1 = this.zs[i + 1] as number;
    out[0] = x0 + (x1 - x0) * t;
    out[1] = (this.ys[i] as number) + ((this.ys[i + 1] as number) - (this.ys[i] as number)) * t;
    out[2] = z0 + (z1 - z0) * t;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const l = Math.hypot(dx, dz) || 1;
    out[3] = dx / l;
    out[4] = dz / l;
  }
}

/** Lissage de Chaikin (extrémités conservées). */
export function chaikin(points: readonly Vec2[], iterations = 2): Vec2[] {
  let pts = points.slice() as Vec2[];
  for (let it = 0; it < iterations; it++) {
    const out: Vec2[] = [pts[0] as Vec2];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i] as Vec2;
      const b = pts[i + 1] as Vec2;
      out.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]);
      out.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    out.push(pts[pts.length - 1] as Vec2);
    pts = out;
  }
  return pts;
}

/** Rééchantillonne une polyligne 2D à pas régulier. */
export function resample(points: readonly Vec2[], step: number): Vec2[] {
  const out: Vec2[] = [points[0] as Vec2];
  let carry = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i] as Vec2;
    const b = points[i + 1] as Vec2;
    const seg = dist2(a, b);
    let s = step - carry;
    while (s < seg) {
      const t = s / seg;
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      s += step;
    }
    carry = seg - (s - step);
  }
  const last = points[points.length - 1] as Vec2;
  const prev = out[out.length - 1] as Vec2;
  if (dist2(prev, last) > step * 0.3) out.push(last);
  else out[out.length - 1] = last;
  return out;
}

export function round(v: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}
